import { ReactNode, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { EmailVerificationScreen } from '@/components/email-verification-screen'
import { LoginForm } from '@/components/login-form'
import { PasswordRecoveryForm } from '@/components/password-recovery-form'
import { SignupForm } from '@/components/signup-form'
import { useAuth } from '@/shared/auth'
import { Loader } from '@/shared/ui/loader'

type AuthView = 'login' | 'signup' | 'passwordRecovery'

export const WithSecretAuth = ({ children }: { children: ReactNode }) => {
    const [wasLoggedInUsingSecret, setWasLoggedInUsingSecret] = useState(false)
    const [authView, setAuthView] = useState<AuthView>('login')
    const { signInWithSecret, isLoggedIn, isLoadingUser, isEmailVerified, user } = useAuth()
    const [searchParams] = useSearchParams()
    const secret = searchParams.get('s')

    useEffect(() => {
        const signIn = async () => {
            // If already logged in (with Firebase Auth), no need to sign in with secret
            if (isLoggedIn && !secret) {
                return
            }

            // If user came with a secret and hasn't been processed yet
            if (secret && !wasLoggedInUsingSecret) {
                setWasLoggedInUsingSecret(true)
                try {
                    await signInWithSecret(secret)
                } catch (error) {
                    console.error('Error during authentication with secret:', error)
                }
            }
        }

        if (!isLoadingUser) {
            signIn()
        }
    }, [secret, wasLoggedInUsingSecret, isLoggedIn, isLoadingUser, signInWithSecret])

    // Reset authView to login when user logs out
    useEffect(() => {
        if (!isLoggedIn && !isLoadingUser) {
            setAuthView('login')
        }
    }, [isLoggedIn, isLoadingUser])

    // Show loading while authentication is in progress
    if (isLoadingUser) {
        return <Loader />
    }

    // Allow access if user is logged in (either with secret or Firebase Auth)
    if (isLoggedIn) {
        // If user is logged in but email not verified, show verification screen
        // Only require verification for email/password users, not for users logged in with secret
        if (!isEmailVerified && user?.providerData?.[0]?.providerId === 'password') {
            return <EmailVerificationScreen />
        }
        // Stable wrapper to avoid React DOM removeChild issues when switching from Loader to content
        return <div className='contents'>{children}</div>
    }

    // If not logged in and no secret, show login form (like WithAuthRequired)
    if (!secret) {
        return (
            <div className='flex min-h-screen items-center justify-center bg-gray-50 p-4'>
                <div className='relative w-full max-w-md'>
                    {authView === 'login' && (
                        <LoginForm
                            onSwitchToSignup={() => setAuthView('signup')}
                            onSwitchToPasswordRecovery={() => setAuthView('passwordRecovery')}
                            onSuccess={() => {
                                // User logged in, component will re-render
                            }}
                        />
                    )}
                    {authView === 'signup' && (
                        <SignupForm
                            onSwitchToLogin={() => setAuthView('login')}
                            onSuccess={() => {
                                // User signed up, will show verification screen
                            }}
                        />
                    )}
                    {authView === 'passwordRecovery' && (
                        <PasswordRecoveryForm
                            onBackToLogin={() => setAuthView('login')}
                            onSwitchToSignup={() => setAuthView('signup')}
                        />
                    )}
                </div>
            </div>
        )
    }

    // If there's a secret but authentication failed or is in progress, show loading
    return <Loader />
}
